import { useTranslation } from 'react-i18next';
import type { MemberLedgerSummaryDto } from '@pokerroom/types';
import { buildLedgerTabs, formatPoints, timedLabel, type LedgerTab } from './ledgerTabs';

/** Balances of every ledger in one row; each item opens its tab. */
export function LedgerSummaryStrip({
  summary,
  wallet = false,
  activeKey,
  onSelect,
}: {
  summary: MemberLedgerSummaryDto;
  wallet?: boolean;
  activeKey: string;
  onSelect: (tab: LedgerTab) => void;
}) {
  const { t } = useTranslation('member');
  // `raw` is the unformatted balance: the owed check must not parse a "-1,000" display string.
  const items = buildLedgerTabs(summary, wallet)
    .filter((tab) => tab.kind !== 'info')
    .flatMap((tab) => {
      if (tab.kind === 'ASSET') {
        const asset = summary.assets!.find((row) => row.assetTypeId === tab.assetTypeId)!;
        const period = asset.periodBalances.find((row) => row.period === tab.period);
        return {
          tab,
          label: `${asset.displayName}${tab.period ? ` ${tab.period}` : ''}`,
          value: formatPoints(period?.balance ?? asset.balance),
          raw: period?.balance ?? asset.balance,
          muted: !asset.enabled || !!period?.expired,
        };
      }
      if (tab.kind === 'ZIPA')
        return {
          tab,
          label: t('ledgerTabZipa'),
          value: `NT$${formatPoints(summary.zipa)}`,
          raw: summary.zipa,
          muted: false,
        };
      if (tab.kind === 'PLAYER')
        return {
          tab,
          label:
            summary?.pointTypes?.find((type) => type.code === 'PLAYER')?.displayName ??
            t('ledgerTabPlayer'),
          value: formatPoints(summary.player),
          raw: summary.player,
          muted: false,
        };
      if (tab.kind === 'PACKAGES')
        return [
          {
            tab,
            key: 'packages',
            label: t('walletSummaryPackages'),
            value: String(summary.packages?.length ?? 0),
            raw: '0',
            muted: false,
          },
          {
            tab,
            key: 'plan',
            label: t('walletSummaryPlan'),
            value: summary.plan ? t('walletPlanPaid') : t('walletNone'),
            raw: '0',
            muted: !summary.plan,
          },
        ];
      if (tab.kind === 'VOUCHERS')
        return {
          tab,
          key: 'vouchers',
          label: t('walletSummaryVouchers'),
          value: String(summary.vouchers ?? 0),
          raw: '0',
          muted: false,
        };
      if (tab.kind === 'Z')
        return {
          tab,
          label:
            summary?.pointTypes?.find((type) => type.code === 'Z')?.displayName ?? t('ledgerTabZ'),
          value: formatPoints(summary.z.balance),
          raw: summary.z.balance,
          muted: false,
        };
      const month = summary.timed.find((m) => m.period === tab.period)!;
      return {
        tab,
        label: summary.pointTypes?.find((type) => type.code === 'TIMED')
          ? `${summary.pointTypes.find((type) => type.code === 'TIMED')!.displayName} ${month.period}`
          : t(
              timedLabel(
                month.period,
                summary.timed.map((m) => m.period),
              ).key,
              timedLabel(
                month.period,
                summary.timed.map((m) => m.period),
              ).values,
            ),
        value: formatPoints(month.balance),
        raw: month.balance,
        muted: month.expired,
      };
    });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(6.5rem,1fr))] gap-2 mb-4">
      {items.map(({ tab, key, label, value, raw, muted }) => (
        <button
          key={key ?? tab.key}
          type="button"
          onClick={() => onSelect(tab)}
          aria-pressed={tab.key === activeKey}
          className={`min-h-11 min-w-0 rounded-lg border px-3 py-2 text-left [overflow-wrap:anywhere] ${
            tab.key === activeKey ? 'border-primary bg-primary/10' : 'border-base-300 bg-base-200'
          }`}
        >
          <span className="block text-xs text-base-content/70">{label}</span>
          <span
            className={`block text-base font-bold tabular-nums ${muted ? 'text-base-content/70' : Number(raw) < 0 ? 'text-error' : 'text-base-content'}`}
          >
            {value}
            {Number(raw) < 0 && <span className="ml-1 text-xs">{t('ledgerOwed')}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}
